import { LuneTheme, Table } from '@lune-fe/lune-ui-lib'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import MUIText from '@mui/material/Typography'
import Image from 'next/image'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import Divider from '@/components/Divider'
import { difference } from '@/components/EtsMap/utils'

import '@fontsource/space-mono'
import '@fontsource/space-grotesk'

import '@fontsource/space-mono/400.css'
import '@fontsource/space-grotesk/700.css'

const mapCombinations = {
    '1': {
        header: 'Asia to Mediterranean, 1 TEU',
        vessels: [
            {
                logo: '/images/carrier-logos/carrier-evergreen.png',
                name: 'EVER Top',
                surcharge: '23.00',
                luneEstimate: '18.57',
                disclaimer: (
                    <>
                        The EVER Top is in service between East Asia and the Mediterranean, via The
                        Cape due to the Red Sea crisis.
                        <br />
                        <br />
                        Our estimate includes 50% of CO₂ emissions produced between Singapore and
                        Piraeus, plus 100% emissions between Piraeus and Genoa, as required by ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-cma-cgm.png',
                name: 'CGA CGM Galapagos',
                surcharge: '20.00',
                luneEstimate: '10.18',
                disclaimer: (
                    <>
                        The CMA CGM Galapagos is in service between East Asia and the Mediterranean,
                        via Suez.
                        <br />
                        <br />
                        Lune’s estimate includes 50% of CO₂ emissions produced between Singapore and
                        Malta, plus 100% emissions between Malta and Genoa, as required by the EU
                        ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-cosco.png',
                name: 'COSCO Nebula',
                surcharge: '19.00',
                luneEstimate: '8.94',
                disclaimer: (
                    <>
                        The COSCO Nebula is in service between East Asia and the Mediterranean, via
                        Suez.
                        <br />
                        <br />
                        Lune’s estimate includes 50% of CO₂ emissions produced between Singapore and
                        Piraeus, plus 100% emissions between Piraeus and Genoa, as required by the
                        EU ETS.
                    </>
                ),
            },
        ],
    },
    '2': {
        header: 'Europe to North America, 1 TEU',
        vessels: [
            {
                logo: '/images/carrier-logos/carrier-cosco.png',
                name: 'COSCO Hope',
                surcharge: '42.00',
                luneEstimate: '8.10',
                disclaimer: (
                    <>
                        The COSCO Hope is in service between northern Europe and north America.
                        <br />
                        <br />
                        Lune’s estimate includes 100% of CO₂ emissions produced between Antwerp and
                        Le Havre, plus 50% emissions between Le Havre and New York, as required by
                        the EU ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-msc.png',
                name: 'MSC Branka',
                surcharge: '35.00',
                luneEstimate: '17.88',
                disclaimer: (
                    <>
                        The MSC Branka is in service between northern Europe and north America.
                        <br />
                        <br />
                        Our estimate includes 100% of CO₂ emissions produced between Hamburg and
                        Sines, plus 50% emissions between Sines and Panama, as required by ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-one.png',
                name: 'ONE Honolulu',
                surcharge: '21.14',
                luneEstimate: '12.31',
                disclaimer: (
                    <>
                        The ONE Honolulu is in service between northern Europe and north America,
                        via Asia.
                        <br />
                        <br />
                        Lune’s estimate includes 50% of CO₂ emissions produced between Hamburg and
                        Singapore, as required by the EU ETS.
                    </>
                ),
            },
        ],
    },
    '3': {
        header: 'Intra-mediterranean, 1 TEU',
        vessels: [
            {
                logo: '/images/carrier-logos/carrier-cma-cgm.png',
                name: 'CMA CGM Michel A',
                surcharge: '25.00',
                luneEstimate: '7.99',
                disclaimer: (
                    <>
                        The CMA CGM Michel A is in service in the Mediterranean.
                        <br />
                        <br />
                        Lune’s estimate includes 50% of CO₂ emissions produced between Beirut and
                        Marseille, as required by the EU ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-msc.png',
                name: 'MSC Fiammetta',
                surcharge: '16.00',
                luneEstimate: '5.92',
                disclaimer: (
                    <>
                        The MSC Fiammetta is in service in the Mediterranean.
                        <br />
                        <br />
                        Lune’s estimate includes 100% of CO₂ emissions produced between Gioia Tauro
                        and Valencia, as required by the EU ETS.
                    </>
                ),
            },
            {
                logo: '/images/carrier-logos/carrier-hapag-lloyds.png',
                name: 'HAPAG-LLOYD Ludwigshafen Express',
                surcharge: '14.00',
                luneEstimate: '4.76',
                disclaimer: (
                    <>
                        The HAPAG-LLOYD Ludwigshafen Express is in service in the Mediterranean.
                        <br />
                        <br />
                        Lune’s estimate includes 50% of CO₂ emissions produced between Damietta and
                        Valencia, plus 100% emissions between Valencia and Genoa, as required by the
                        EU ETS.
                    </>
                ),
            },
        ],
    },
}

const Header: FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <MUIText
            color={'Blue500'}
            sx={{
                fontFamily: 'Space Grotesk',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: '100%',
            }}
        >
            {children}
        </MUIText>
    )
}
const Text: FC<{ children: React.ReactNode; sx?: SxProps }> = ({ children, sx }) => {
    return (
        <MUIText
            color={'Blue500'}
            sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontFamily: 'Space Mono',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '100%',
                ...sx,
            }}
        >
            {children}
        </MUIText>
    )
}

const MapLegendBox: FC<{
    selection: '1' | '2' | '3'
    onSubSelectionChange: (idx: '1' | '2' | '3') => void
}> = ({ selection, onSubSelectionChange }) => {
    const { palette, spacing } = LuneTheme
    const { header, vessels } = mapCombinations[selection]
    const [subSelection, setSubSelection] = useState<number>(1)
    const [isHovered, setIsHovered] = useState<boolean>(false)

    const moveRow = useCallback(() => {
        setSubSelection((prev) => {
            const next = (prev % 3) + 1
            onSubSelectionChange(`${next}` as '1' | '2' | '3')
            return next
        })
    }, [onSubSelectionChange])

    const disclaimer = useMemo(() => {
        return mapCombinations[selection].vessels[subSelection - 1].disclaimer
    }, [selection, subSelection])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!isHovered) {
                moveRow()
            }
        }, 6000)
        return () => clearTimeout(timer)
    }, [subSelection, isHovered, moveRow])

    const handleMouseEnter = (sel: number) => {
        setIsHovered(true)
        setSubSelection(sel)
        onSubSelectionChange(`${sel}` as '1' | '2' | '3')
    }

    const handleMouseLeave = () => {
        setIsHovered(false)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                fontFamily: 'Space Grotesk',
                p: 3,
                backgroundColor: `white`,
                color: palette.Blue500,
                border: '1px solid',
                borderColor: palette.Blue500,
                borderRadius: '24px',
                position: 'absolute',
                left: '40px',
                top: '128px',
                gap: 5,
                width: '520px',
                fontSize: '20px',
            }}
        >
            <MUIText
                color={`Blue500`}
                sx={{
                    fontFamily: 'Space Grotesk',
                    fontSize: '28px',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    lineHeight: '120%',
                }}
            >
                {header}
            </MUIText>
            <Divider />
            {vessels.length > 0 && (
                <Table
                    sx={{
                        width: '100%',
                        '& .MuiTableCell-root div': {
                            minHeight: '36px !important',
                        },
                        '& .MuiTableRow-hover': {
                            backgroundColor: `${palette.Blue50} !important`,
                        },
                    }}
                >
                    <Table.TableHead sx={{ width: '100%' }}>
                        <Table.TableRow sx={{ width: '100$' }}>
                            <Table.TableCell>
                                <Header>Vessel</Header>
                            </Table.TableCell>
                            <Table.TableCell align="right">
                                <Header>Carrier surcharge</Header>
                            </Table.TableCell>
                            <Table.TableCell align="right">
                                <Header>Lune estimate</Header>
                            </Table.TableCell>
                            <Table.TableCell alignEnd align="right">
                                <Header>Difference</Header>
                            </Table.TableCell>
                        </Table.TableRow>
                    </Table.TableHead>
                    <Table.TableBody>
                        {vessels.map(({ logo, name, surcharge, luneEstimate }, i) => (
                            <Table.TableRow
                                key={i}
                                className={subSelection === i + 1 ? 'MuiTableRow-hover' : ''}
                                onMouseEnter={() => handleMouseEnter(i + 1)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <Table.TableCell>
                                    <Image
                                        style={{ marginRight: spacing(1) }}
                                        alt={name}
                                        src={logo}
                                        width={12}
                                        height={12}
                                    />{' '}
                                    <Text sx={{ width: '142px' }}>{name}</Text>
                                </Table.TableCell>
                                <Table.TableCell alignEnd align="right">
                                    <Text>&euro; {surcharge}</Text>
                                </Table.TableCell>
                                <Table.TableCell alignEnd align="right">
                                    <Text>&euro; {luneEstimate}</Text>
                                </Table.TableCell>
                                <Table.TableCell alignEnd align="right">
                                    <Text>{difference(surcharge, luneEstimate)} %</Text>
                                </Table.TableCell>
                            </Table.TableRow>
                        ))}
                    </Table.TableBody>
                </Table>
            )}
            <Divider />
            <MUIText
                color={`Blue500`}
                sx={{
                    fontFamily: 'space Grotesk',
                    fontSize: '10px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '120%',
                }}
            >
                {disclaimer}
            </MUIText>
        </Box>
    )
}

export default MapLegendBox
